<template>
  <div class="min-h-screen bg-white">
    <TrustBar />
    <div class="pt-18"></div>
    <!-- Loading State -->
    <div v-if="loading" class="min-h-screen flex items-center justify-center">
      <div class="text-center">
        <div
          class="inline-block w-12 h-12 border-4 border-fuchsia-600 border-t-transparent rounded-full animate-spin"
        ></div>
        <p class="mt-4 text-gray-600">Chargement...</p>
      </div>
    </div>

    <!-- Error State -->
    <div
      v-else-if="error"
      class="min-h-screen flex items-center justify-center"
    >
      <div class="text-center">
        <div class="text-7xl mb-6">⚠️</div>
        <h3 class="text-2xl font-bold text-gray-900 mb-3">{{ error }}</h3>
        <router-link
          to="/mobile-top-up"
          class="inline-block px-6 py-3 bg-fuchsia-600 text-white rounded-xl hover:bg-fuchsia-700 transition"
        >
          Retour à la recharge mobile
        </router-link>
      </div>
    </div>

    <!-- Content -->
    <template v-else>
      <!-- Breadcrumb -->
      <div class="bg-gray-50 py-3 border-b border-gray-200">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav class="flex text-sm">
            <router-link to="/" class="text-gray-500 hover:text-gray-900"
              >Accueil</router-link
            >
            <span class="mx-2 text-gray-400">/</span>
            <router-link
              to="/mobile-top-up"
              class="text-gray-500 hover:text-gray-900"
              >Recharge mobile</router-link
            >
            <span class="mx-2 text-gray-400">/</span>
            <span class="text-gray-900 font-medium">{{
              operator?.name || operator?.operatorName
            }}</span>
          </nav>
        </div>
      </div>

      <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div class="grid gap-8 lg:gap-12 lg:grid-cols-12 lg:mt-0">
          <!-- Left Column: Product Image -->
          <div class="lg:col-span-5 relative lg:mt-12 mt-8">
            <div
              class="flex justify-center lg:flex-col gap-4 lg:sticky lg:top-32"
            >
              <div class="relative">
                <!-- Gradient background effect -->
                <div
                  class="absolute right-1/4 top-1/2 -translate-y-1/2 h-[calc(100%+3.5rem)] w-[200vw] -z-10 rounded-full lg:h-[calc(100%+10.5rem)] lg:-top-8 lg:translate-y-0 bg-gradient-to-r from-gray-100 via-gray-100 to-transparent/5"
                ></div>
                <img
                  :alt="operator?.name || operator?.operatorName"
                  class="rounded-xl relative h-36 md:h-48 lg:h-64 w-auto lg:w-[400px] shadow-[0_0_4px_rgba(0,0,0,0.25)]"
                  :src="operatorLogo"
                />
              </div>
              <!-- USP Badges (Desktop) -->
              <div class="hidden lg:block relative">
                <ul class="flex flex-wrap gap-2 items-center lg:items-start">
                  <li
                    class="inline-flex items-center gap-2 bg-gray-100 h-8 rounded-full px-4 w-auto"
                  >
                    <svg
                      class="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      />
                    </svg>
                    <span class="text-xs">Livraison en ligne instantanée</span>
                  </li>
                  <li
                    class="inline-flex items-center gap-2 bg-gray-100 h-8 rounded-full px-4 w-auto"
                  >
                    <svg
                      class="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                      />
                    </svg>
                    <span class="text-xs">Paiement sûr et sécurisé</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <!-- Right Column: Form -->
          <div
            class="lg:col-span-7 flex flex-1 flex-col items-center lg:items-start mt-3 lg:mt-2"
          >
            <div class="w-full flex flex-1 flex-col mb-5 lg:mb-6">
              <form @submit.prevent="goToCheckout" class="flex flex-col">
                <!-- Title -->
                <div class="flex flex-row flex-wrap gap-x-2 items-center">
                  <h1 class="font-bold text-xl md:text-3xl mb-2">
                    {{ operator?.name || operator?.operatorName }}
                    {{ country?.countryName || '' }}
                  </h1>
                  <p v-if="operator?.destinationCurrencyCode && operator?.destinationCurrencyCode !== country?.currency" class="text-sm text-gray-600">
                    Le destinataire recevra le montant en {{ operator.destinationCurrencyCode }}
                  </p>
                </div>

                <!-- Country and Delivery Info -->
                <div class="flex flex-wrap gap-3 md:gap-4 mt-5">
                  <!-- Country Selector -->
                  <div class="flex flex-1 min-w-[200px] md:flex-initial">
                    <button
                      type="button"
                      class="rounded-2xl flex items-center flex-1 justify-between border border-gray-200 bg-white px-4 py-3 hover:border-gray-300 hover:shadow-sm transition-all duration-200"
                    >
                      <div class="flex items-center gap-3">
                        <div
                          class="flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl w-11 h-11 shadow-inner"
                        >
                          <span v-if="country?.localLogoUrl">
                            <img
                              :src="country.localLogoUrl"
                              :alt="country.countryName"
                              class="w-6 h-4 object-cover rounded-sm"
                            />
                          </span>
                          <span
                            v-else
                            class="text-base font-bold text-gray-700"
                            >{{ country?.countryCode || 'BJ' }}</span
                          >
                        </div>
                        <div class="text-left">
                          <span class="text-xs text-gray-500 block"
                            >Pays d'utilisation</span
                          >
                          <span class="text-sm font-bold text-gray-900">{{
                            country?.countryName || 'Bénin'
                          }}</span>
                        </div>
                      </div>
                    </button>
                  </div>
                  <!-- Delivery Type -->
                  <div class="flex flex-1 min-w-[200px] md:flex-initial">
                    <button
                      type="button"
                      class="rounded-2xl flex items-center flex-1 justify-between border border-gray-200 bg-white px-4 py-3 hover:border-gray-300 hover:shadow-sm transition-all duration-200"
                    >
                      <div class="flex items-center gap-3">
                        <div
                          class="flex items-center justify-center bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl w-11 h-11 shadow-inner"
                        >
                          <svg
                            class="w-6 h-6 text-green-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                            />
                          </svg>
                        </div>
                        <div class="text-left">
                          <span class="text-xs text-gray-500 block"
                            >Livraison</span
                          >
                          <span class="text-sm font-bold text-gray-900"
                            >Envoi sur téléphone</span
                          >
                        </div>
                      </div>
                    </button>
                  </div>
                </div>

                <!-- Phone Number Input -->
                <div class="flex flex-col mb-4 lg:mb-6">
                  <div class="mt-6 mb-2">
                    <label
                      class="block text-gray-900 text-sm font-semibold mb-2"
                      for="phoneNumber"
                    >
                      Numéro de téléphone du destinataire
                    </label>
                    <div
                      class="flex border-2 border-gray-200 rounded-2xl h-14 bg-white max-w-full overflow-hidden transition-all duration-200 focus-within:border-fuchsia-500 focus-within:ring-4 focus-within:ring-fuchsia-100"
                    >
                      <div class="border-r border-gray-200 bg-gray-50 px-2">
                        <button
                          type="button"
                          class="h-full flex items-center gap-2 px-2"
                        >
                          <span v-if="country?.localLogoUrl">
                            <img
                              :src="country.localLogoUrl"
                              :alt="country.countryName"
                              class="w-6 h-5 object-cover rounded-sm shadow-sm"
                            />
                          </span>
                          <span v-else class="text-lg">{{
                            getCountryFlag()
                          }}</span>
                          <span class="font-bold text-base text-gray-900">{{
                            phoneCode
                          }}</span>
                          <svg
                            class="w-4 h-4 text-gray-500"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </button>
                      </div>
                      <div class="flex-1">
                        <input
                          v-model="phoneNumber"
                          id="phoneNumber"
                          type="tel"
                          placeholder="01 95 12 34 56"
                          class="w-full h-full px-4 bg-transparent outline-none placeholder-gray-400 font-semibold text-gray-900"
                        />
                      </div>
                    </div>
                    <p
                      v-if="phoneError"
                      class="text-red-500 text-sm mt-2 flex items-center gap-1"
                    >
                      <svg
                        class="w-4 h-4"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                          clip-rule="evenodd"
                        />
                      </svg>
                      {{ phoneError }}
                    </p>
                  </div>
                </div>

                <!-- Amount Selection -->
                <div class="mt-6">
                  <fieldset>
                    <legend class="sr-only">Sélectionnez un montant</legend>
                    <div
                      class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
                    >
                      <div
                        v-for="preset in amountPresets"
                        :key="preset"
                        @click="
                          selectedAmount = preset;
                        "
                        class="relative overflow-hidden rounded-2xl bg-white border-2 p-5 cursor-pointer transition-all duration-200 group"
                        :class="
                          selectedAmount === preset
                            ? 'border-fuchsia-600 shadow-lg shadow-fuchsia-100 scale-[1.02]'
                            : 'border-gray-200 hover:border-fuchsia-300 hover:shadow-md'
                        "
                      >
                        <div
                          class="absolute inset-0 bg-gradient-to-br transition-opacity duration-200"
                          :class="
                            selectedAmount === preset
                              ? 'from-fuchsia-50 to-pink-50 opacity-100'
                              : 'from-gray-50 to-transparent opacity-0 group-hover:opacity-100'
                          "
                        ></div>
                        <div class="relative">
                          <h4
                            class="font-semibold text-base mb-3"
                            :class="
                              selectedAmount === preset
                                ? 'text-fuchsia-700'
                                : 'text-gray-900'
                            "
                          >
                            {{ formatPrice(preset) }}
                          </h4>
                          <button
                            type="button"
                            class="w-full h-11 rounded-xl font-semibold text-sm flex justify-center items-center transition-all duration-200"
                            :class="
                              selectedAmount === preset
                                ? 'bg-fuchsia-600 text-white shadow-md'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            "
                          >
                            Acheter
                          </button>
                        </div>
                      </div>
                    </div>

                    <!-- Custom Amount Input (for range amounts) -->
                    <div v-if="supportsRangeAmount" class="mt-5">
                      <label
                        class="block text-gray-900 text-sm font-semibold mb-2"
                      >
                        Ou entrez un montant personnalisé ({{
                          formatPrice(minAmount)
                        }}
                        - {{ formatPrice(maxAmount) }})
                      </label>
                      <div class="flex gap-3 items-center">
                        <div class="flex-1 relative">
                          <input
                            v-model="customAmount"
                            @input="validateCustomAmount"
                            type="number"
                            :min="minAmount"
                            :max="maxAmount"
                            :step="
                              operator.value?.senderDenominationStep || 100
                            "
                            placeholder="Entrez un montant"
                            class="w-full border-2 border-gray-200 rounded-2xl h-14 px-4 pr-20 font-semibold text-gray-900 placeholder:font-normal placeholder-gray-400 transition-all duration-200 focus:border-fuchsia-500 focus:ring-4 focus:ring-fuchsia-100 outline-none"
                            @focus="
                              selectedAmount = parseFloat(customAmount) || null
                            "
                          />
                          <span
                            class="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium"
                            >{{ country?.currency || 'FCFA' }}</span
                          >
                        </div>
                      </div>
                      <p
                        v-if="customAmountError"
                        class="text-red-500 text-sm mt-2 flex items-center gap-1"
                      >
                        <svg
                          class="w-4 h-4"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fill-rule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                            clip-rule="evenodd"
                          />
                        </svg>
                        {{ customAmountError }}
                      </p>
                    </div>
                  </fieldset>
                </div>

                <!-- Email Input -->
                <div class="mt-6 mb-4">
                  <label
                    class="block text-gray-900 text-sm font-semibold mb-2"
                    for="email"
                  >
                    Adresse e-mail
                  </label>
                  <input
                    v-model="email"
                    id="email"
                    type="email"
                    placeholder="votre@email.com"
                    class="w-full border-2 border-gray-200 rounded-2xl h-14 px-4 font-semibold text-gray-900 placeholder:font-normal placeholder-gray-400 transition-all duration-200 focus:border-fuchsia-500 focus:ring-4 focus:ring-fuchsia-100 outline-none"
                  />
                  <p
                    v-if="emailError"
                    class="text-red-500 text-sm mt-2 flex items-center gap-1"
                  >
                    <svg
                      class="w-4 h-4"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                        clip-rule="evenodd"
                      />
                    </svg>
                    {{ emailError }}
                  </p>
                </div>

                <!-- Payment Methods -->
                <div
                  class="flex flex-col gap-4 items-center justify-centerlg:items-start mb-6"
                >
                  <div class="flex items-center gap-2">
                    <ul class="flex gap-2 p-0 m-0 list-none">
                      <li
                        class="border border-gray-200 rounded-lg p-1.5 bg-white flex items-center justify-center w-10 h-8 shadow-sm"
                      >
                        <svg class="h-4 w-auto" viewBox="0 0 36 16" fill="none">
                          <text
                            x="0"
                            y="12"
                            font-size="10"
                            font-weight="bold"
                            fill="#1A1F71"
                          >
                            VISA
                          </text>
                        </svg>
                      </li>
                      <li
                        class="border border-gray-200 rounded-lg p-1.5 bg-white flex items-center justify-center w-10 h-8 shadow-sm"
                      >
                        <svg class="h-4 w-auto" viewBox="0 0 36 16" fill="none">
                          <circle cx="12" cy="8" r="6" fill="#EB001B" />
                          <circle cx="24" cy="8" r="6" fill="#F79E1B" />
                        </svg>
                      </li>
                      <li
                        class="border border-gray-200 rounded-lg p-1.5 bg-white flex items-center justify-center w-10 h-8 shadow-sm"
                      >
                        <svg class="h-4 w-auto" viewBox="0 0 36 16" fill="none">
                          <text
                            x="0"
                            y="12"
                            font-size="8"
                            font-weight="bold"
                            fill="#003087"
                          >
                            PayPal
                          </text>
                        </svg>
                      </li>
                    </ul>
                  </div>
                </div>

                <!-- Submit Button -->
                <div class="mt-4">
                  <button
                    type="submit"
                    class="w-full md:w-auto md:min-w-[280px] h-14 bg-gradient-to-r from-fuchsia-600 to-pink-600 hover:from-fuchsia-700 hover:to-pink-700 text-white font-bold rounded-full px-8 transition-all duration-200 shadow-lg shadow-fuchsia-200 hover:shadow-xl hover:shadow-fuchsia-300 flex items-center justify-center gap-2 active:scale-[0.98]"
                    :disabled="!selectedAmount"
                    :class="{
                      'opacity-50 cursor-not-allowed shadow-none hover:shadow-none active:scale-100':
                        !selectedAmount
                    }"
                  >
                    <span>Continuer vers le paiement</span>
                    <svg
                      class="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M14 5l7 7m0 0l-7 7m7-7H3"
                      />
                    </svg>
                  </button>
                  <p v-if="!selectedAmount" class="text-gray-500 text-sm mt-2">
                    Sélectionnez un montant pour continuer
                  </p>
                </div>
              </form>

              <!-- USP Badges (Mobile) -->
              <div class="lg:hidden mt-6">
                <ul class="flex flex-wrap gap-2 items-center">
                  <li
                    class="inline-flex items-center gap-2 bg-gray-100 h-8 rounded-full px-4"
                  >
                    <svg
                      class="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      />
                    </svg>
                    <span class="text-xs">Livraison en ligne instantanée</span>
                  </li>
                  <li
                    class="inline-flex items-center gap-2 bg-gray-100 h-8 rounded-full px-4"
                  >
                    <svg
                      class="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                      />
                    </svg>
                    <span class="text-xs">Paiement sûr et sécurisé</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <!-- About Section -->
        <aside class="lg:col-span-10 lg:col-start-2 mt-8 lg:mt-10">
          <div class="grid gap-8 lg:gap-12 lg:grid-cols-12">
            <div class="lg:col-span-5">
              <section>
                <h2 class="font-bold text-xl md:text-2xl mb-4">
                  À propos de {{ operator?.name || operator?.operatorName }}
                </h2>
                <div class="text-gray-600 space-y-4">
                  <p>
                    Rechargez votre forfait prépayé
                    {{ operator?.name || operator?.operatorName }} en quelques
                    clics. Sélectionnez simplement le montant dont vous avez
                    besoin et entrez votre numéro de téléphone.
                  </p>
                  <p>
                    Lorsque le paiement est terminé, votre solde sera rechargé
                    immédiatement ! C'est rapide, sûr et simple.
                  </p>
                </div>
              </section>
              <div class="mt-4 lg:mt-4 text-sm text-gray-500">
                <span>En utilisant ce service, vous acceptez les </span>
                <a href="#" class="underline hover:text-gray-900"
                  >Conditions générales</a
                >
                <span> de {{ operator?.name || operator?.operatorName }}.</span>
              </div>
            </div>

            <!-- FAQ Section -->
            <div class="lg:col-span-7 flex flex-col">
              <section>
                <h2 class="font-bold text-xl md:text-2xl mb-5">
                  Questions fréquemment posées
                </h2>
                <div class="space-y-3">
                  <!-- FAQ Item 1 -->
                  <div
                    class="rounded-2xl bg-white border-2 border-gray-200 overflow-hidden transition-all duration-200"
                    :class="{ 'border-fuchsia-200 shadow-md': openFaq === 0 }"
                  >
                    <button
                      @click="toggleFaq(0)"
                      class="w-full text-left p-5 flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
                    >
                      <h3 class="font-semibold text-base text-gray-900 pr-4">
                        Comment recharger mon code prépayé ?
                      </h3>
                      <div
                        class="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full transition-all duration-200"
                        :class="
                          openFaq === 0
                            ? 'bg-fuchsia-100 rotate-180'
                            : 'bg-gray-100'
                        "
                      >
                        <svg
                          class="w-4 h-4"
                          :class="
                            openFaq === 0 ? 'text-fuchsia-700' : 'text-gray-600'
                          "
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </button>
                    <div v-show="openFaq === 0" class="px-5 pb-5 pt-0">
                      <ol
                        class="pl-4 list-decimal space-y-2 text-gray-600 leading-relaxed"
                      >
                        <li>Sélectionnez le montant.</li>
                        <li>
                          Entrez votre numéro de téléphone et votre adresse
                          e-mail.
                        </li>
                        <li>
                          Payez votre commande et recevez la recharge
                          instantanément.
                        </li>
                      </ol>
                    </div>
                  </div>

                  <!-- FAQ Item 2 -->
                  <div
                    class="rounded-2xl bg-white border-2 border-gray-200 overflow-hidden transition-all duration-200"
                    :class="{ 'border-fuchsia-200 shadow-md': openFaq === 1 }"
                  >
                    <button
                      @click="toggleFaq(1)"
                      class="w-full text-left p-5 flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
                    >
                      <h3 class="font-semibold text-base text-gray-900 pr-4">
                        Comment vérifier mon solde ?
                      </h3>
                      <div
                        class="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full transition-all duration-200"
                        :class="
                          openFaq === 1
                            ? 'bg-fuchsia-100 rotate-180'
                            : 'bg-gray-100'
                        "
                      >
                        <svg
                          class="w-4 h-4"
                          :class="
                            openFaq === 1 ? 'text-fuchsia-700' : 'text-gray-600'
                          "
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </button>
                    <div v-show="openFaq === 1" class="px-5 pb-5 pt-0">
                      <p class="text-gray-600 leading-relaxed">
                        Composez *124# suivi du bouton d'appel pour vérifier
                        votre solde.
                      </p>
                    </div>
                  </div>

                  <!-- FAQ Item 3 -->
                  <div
                    class="rounded-2xl bg-white border-2 border-gray-200 overflow-hidden transition-all duration-200"
                    :class="{ 'border-fuchsia-200 shadow-md': openFaq === 2 }"
                  >
                    <button
                      @click="toggleFaq(2)"
                      class="w-full text-left p-5 flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
                    >
                      <h3 class="font-semibold text-base text-gray-900 pr-4">
                        Comment contacter le service client ?
                      </h3>
                      <div
                        class="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full transition-all duration-200"
                        :class="
                          openFaq === 2
                            ? 'bg-fuchsia-100 rotate-180'
                            : 'bg-gray-100'
                        "
                      >
                        <svg
                          class="w-4 h-4"
                          :class="
                            openFaq === 2 ? 'text-fuchsia-700' : 'text-gray-600'
                          "
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </button>
                    <div v-show="openFaq === 2" class="px-5 pb-5 pt-0">
                      <ul
                        class="pl-4 list-disc space-y-2 text-gray-600 leading-relaxed"
                      >
                        <li>Appelez le service client depuis votre mobile</li>
                        <li>Visitez le site web de l'opérateur</li>
                        <li>Contactez via les réseaux sociaux</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </aside>
      </main>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, getCurrentInstance } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { Countries } from '@features/shared/libraries/countries';

const route = useRoute();
const router = useRouter();
const { proxy } = getCurrentInstance();
const { $stores } = proxy;

// Data
const operator = ref(null);
const country = ref(null);
const loading = ref(true);
const error = ref(null);
const operators = ref([]);

// Form state
const phoneNumber = ref('');
const email = ref('');
const selectedAmount = ref(null);
const phoneError = ref('');
const emailError = ref('');

// FAQ state
const openFaq = ref(null);

const toggleFaq = (index) => {
  openFaq.value = openFaq.value === index ? null : index;
};

// Parse slug from URL (format: operatorId-countryCode)
const parseSlug = () => {
  const slug = route.params.slug;
  if (!slug) return null;

  const parts = slug.split('-');
  if (parts.length < 2) return null;

  const operatorId = parseInt(parts[0]);
  const countryCode = parts[1].toUpperCase();

  return { operatorId, countryCode };
};

// Amount presets - use localFixedAmounts from API if available
const amountPresets = computed(() => {
  // Use localFixedAmounts from operator API
  if (operator.value?.localFixedAmounts && operator.value.localFixedAmounts.length > 0) {
    return operator.value.localFixedAmounts;
  }
  // Fallback to currency-based defaults
  const currency = country.value?.currency || 'FCFA';
  if (currency === 'XOF') {
    return [2500, 5000, 10000, 15000, 20000, 25000];
  }
  return [500, 1000, 2000, 5000, 10000, 20000];
});

// Check if operator supports range amounts
const supportsRangeAmount = computed(() => {
  return (
    operator.value?.denominationType === 'RANGE' ||
    (operator.value?.localMinAmount && operator.value?.localMaxAmount)
  );
});

const minAmount = computed(() => {
  return (
    operator.value?.localMinAmount ||
    operator.value?.minSenderDenomination ||
    amountPresets.value[0]
  );
});

const maxAmount = computed(() => {
  return (
    operator.value?.localMaxAmount ||
    operator.value?.maxSenderDenomination ||
    amountPresets.value[amountPresets.value.length - 1]
  );
});

const customAmount = ref('');
const customAmountError = ref('');

const validateCustomAmount = () => {
  if (!customAmount.value) {
    customAmountError.value = '';
    selectedAmount.value = null;
    return;
  }

  const amount = parseFloat(customAmount.value);
  if (isNaN(amount)) {
    customAmountError.value = 'Veuillez entrer un montant valide';
    selectedAmount.value = null;
    return;
  }

  if (amount < minAmount.value) {
    customAmountError.value = `Montant minimum: ${formatPrice(minAmount.value)}`;
    selectedAmount.value = null;
    return;
  }

  if (amount > maxAmount.value) {
    customAmountError.value = `Montant maximum: ${formatPrice(maxAmount.value)}`;
    selectedAmount.value = null;
    return;
  }

  customAmountError.value = '';
  selectedAmount.value = amount;
};

const phoneCode = computed(() => {
  return (
    country.value?.phonePrefix || `+${country.value?.callingCode}` || '+229'
  );
});

const operatorLogo = computed(() => {
  const logos = operator.value?.logoUrls;
  if (logos && logos.length > 0) {
    return logos[1] || logos[0];
  }
  return '/images/placeholder.png';
});

const getCountryFlag = () => {
  if (!country.value) return '🇧🇯';
  const countryCode = country.value.countryCode;
  const flags = {
    BJ: '🇧🇯',
    TG: '🇹🇬',
    CI: '🇨🇮',
    SN: '🇸🇳',
    ML: '🇲🇱',
    FR: '🇫🇷',
    BE: '🇧🇪',
    CH: '🇨🇭',
    CM: '🇨🇲',
    CD: '🇨🇩',
    NE: '🇳🇪',
    BF: '🇧🇫',
    GN: '🇬🇳'
  };
  return flags[countryCode] || '🌍';
};

const formatPrice = (price) => {
  const currency = country.value?.currency || 'FCFA';
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0
  }).format(price);
};

const validateForm = () => {
  let isValid = true;

  if (!phoneNumber.value || phoneNumber.value.length < 8) {
    phoneError.value = 'Veuillez entrer un numéro de téléphone valide';
    isValid = false;
  } else {
    phoneError.value = '';
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!email.value || !emailRegex.test(email.value)) {
    emailError.value = 'Veuillez entrer une adresse e-mail valide';
    isValid = false;
  } else {
    emailError.value = '';
  }

  if (!selectedAmount.value) {
    isValid = false;
  }

  return isValid;
};

const goToCheckout = () => {
  if (!validateForm()) {
    return;
  }

  router.push({
    path: '/checkout/quick',
    query: {
      operatorId: operator.value?.operatorId || operator.value?.id,
      amount: selectedAmount.value,
      email: email.value,
      phone: phoneNumber.value,
      orderType: 'airtime',
      operationName: 'airtime',
      countryCode: country.value?.countryCode || 'BJ'
    }
  });
};

const fetchOperators = async (countryCode) => {
  loading.value = true;
  error.value = null;

  try {
    const { bus } = await import('@core/events/eventSystem');

    return new Promise((resolve, reject) => {
      let timeoutId;
      let handled = false;

      const cleanup = () => {
        if (timeoutId) clearTimeout(timeoutId);
        bus.off('products.fetch_operators_success', successHandler);
        bus.off('products.fetch_operators_error', errorHandler);
      };

      const successHandler = (data) => {
        if (handled) return;
        handled = true;
        cleanup();
        operators.value = data.data?.operatorList || data.data || data || [];
        loading.value = false;
        resolve();
      };

      const errorHandler = (err) => {
        if (handled) return;
        handled = true;
        cleanup();
        error.value = 'Erreur lors du chargement des opérateurs';
        loading.value = false;
        reject(err);
      };

      // Set timeout for 10 seconds
      timeoutId = setTimeout(() => {
        if (handled) return;
        handled = true;
        cleanup();
        error.value = "Délai d'attente dépassé. Veuillez réessayer.";
        loading.value = false;
        reject(new Error('Timeout'));
      }, 10000);

      bus.on('products.fetch_operators_success', successHandler);
      bus.on('products.fetch_operators_error', errorHandler);

      bus.emit('products.fetch_operators', {
        countryCode: countryCode,
        type: 'airtime'
      });
    });
  } catch (e) {
    error.value = 'Erreur lors du chargement des opérateurs';
    loading.value = false;
  }
};

onMounted(async () => {
  const parsed = parseSlug();

  if (!parsed) {
    error.value = 'URL invalide';
    loading.value = false;
    return;
  }

  const { operatorId, countryCode } = parsed;

  // Find country from library
  const foundCountry = Countries.find(countryCode);
  if (!foundCountry) {
    error.value = 'Pays non trouvé';
    loading.value = false;
    return;
  }

  country.value = foundCountry;

  // Update global store with selected country
  $stores.global.selectCountry(countryCode);

  // Fetch operators from API
  await fetchOperators(countryCode);

  // Find the specific operator
  const foundOperator = operators.value.find(
    (op) => op.operatorId === operatorId
  );
  if (!foundOperator) {
    error.value = 'Opérateur non trouvé';
    loading.value = false;
    return;
  }

  operator.value = foundOperator;

  // Set default amount
  if (amountPresets.value.length > 0) {
    selectedAmount.value = amountPresets.value[1];
  }
});
</script>

<style scoped>
/* Custom transitions for FAQ */
.bg-gray-100 > div {
  transition: all 0.3s ease;
}
</style>
